import { CANCEL_STATUS, DOCK_TYPES, getLatestIvTime, getMinutesFromMidnight, isPet } from "./enviroments"
import { dockModalityPriority, isPetCourse, nonScheduleModality, parseModalityAll } from "./dockCourses"
import { calcDockSchedule, checkEsLowerFull, checkGynecology, checkLockerFull, duplicateCheck, getConsultingSlot, getDockModalityTime, getDockStay, getHolidayName, sortDockContents, CONSULTING_ORDER, DOCK_EXAM_END, ES_LOWER_DAILY_LIMIT, MAX_WAIT_MINUTES, WaitOver } from "lib/dockSchedule"
import { getSamedayArea } from "./dateUtils"

/**
 * ドック予約の候補日検索
 * bookings の実績からコースごとの検査構成を推定し、
 * /dock 画面の「再計算」と同じ自動スケジュールで、登録時のチェック（重複・PETのIV）と運用ルール（更衣室・ES下部の件数など）を通る最短日を探す
 */

export type Gender = "M" | "F"

// docks.course_contents と同じ形
export type CourseContent = {
  modality: string
  modality_body1: string
  modality_body2: string
  modality_contrast: string
}

export type CoursePattern = {
  contents: CourseContent[]  // 優先順位順
  count: number  // このパターンで実施されたドック件数
  total: number  // 母数となるドック件数
}

export type CourseOption = {
  content: CourseContent
  count: number  // この検査を含むドック件数
}

export type CourseProfile = {
  courseName: string
  count: number
  patterns: Partial<Record<Gender | "ALL", CoursePattern>>
  options: Partial<Record<Gender | "ALL", CourseOption[]>>  // コースで実施された検査の一覧（優先順位順）
  receptionTimes: string[]  // 実績のある受付時刻(HH:mm)
}

// docks から取得するドックごとの情報
export type DockInfo = {
  gender?: Gender
  receptionTime?: string
}

// 候補検索の対象者
export type Person = {
  courseName: string
  gender: Gender
  contents: CourseContent[]
  receptionTimes: string[]
  maxStayMinutes?: number  // 受付から全検査終了までの上限(分)。超える受付時刻は NG（2日連続の2日目など）
}

// コースごとの、受付から全検査終了までの上限(分)。超える受付時刻・日は候補にしない（先方の運用ルール 2026/10）
export const COURSE_MAX_STAY_MINUTES: [string, number][] = [
  ['CMC会員コース', 6 * 60],
  ['CMC後日', 4 * 60],
  ['プレミアムがんPETドック', 5 * 60],
  ['がんPETドック', 3.5 * 60],
  ['スーパードック', 4 * 60],
  ['スタンダードドック', 3 * 60],
  ['イーウェル人間ドック', 3 * 60],
  ['レディースドック', 1.5 * 60],
  ['ブレストドック', 1 * 60],
]

// コース名の【2026】【ベイクルーズ男性】などの括弧書き・■などの記号・空白を除き、全角英数字を半角にそろえる
const normalizeCourseName = (courseName: string) => courseName
  .normalize('NFKC')
  .replace(/【[^】]*】/g, '')
  .replace(/[■□◆◇●○★☆※\s]/g, '')

/**
 * コースの所要時間の上限(分)。上の表の名前を含むコース（長い名前から照合）。表にないコースは undefined（上限なし）
 * 例: 「■CMC後日検査」→ CMC後日、「【2026】プレミアムがんPETドック」→ プレミアムがんPETドック
 */
export const getCourseMaxStayMinutes = (courseName: string) => {
  const name = normalizeCourseName(courseName)
  return [...COURSE_MAX_STAY_MINUTES]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([key]) => name.includes(normalizeCourseName(key)))?.[1]
}

// 診療予約（問診・子宮細胞診・HPV）。予約確定時は bookings の modalityID がこの名前で登録される
const CONSULTING_MODALITIES: Record<string, string> = { INTERVIEW: 'QST', UTERINE: 'UCT', HPV: 'HPV' }

/**
 * 再計算の優先順位順に並べ、最後に診療予約（問診・子宮細胞診・HPV）を付ける
 */
const sortCourseContents = (contents: CourseContent[]): CourseContent[] => [
  ...sortDockContents(contents, dockModalityPriority) as CourseContent[],
  ...CONSULTING_ORDER.flatMap(m => contents.filter(v => v.modality === m)),
]

export type ScheduledExam = {
  modalityID: string
  body1: string
  body2: string
  contrast: string
  start: Date
  end: Date
  noSchedule: boolean
  ivCheckin?: string
}

export type PersonSchedule = {
  index: number  // 入力順(0始まり)
  gender: Gender
  reception: Date
  exams: ScheduledExam[]
  stayMinutes: number  // 受付から全検査終了まで(分)
  warnings?: string[]  // 予約はできるが注意が必要なこと（3階更衣室が満室など）
}

export type Candidate = {
  date: Date
  persons: PersonSchedule[]
  second?: { date: Date, persons: PersonSchedule[] }  // 2日連続の場合の2日目（翌営業日）
}

// 1日目のコース → 2日目のコースの組み合わせ（同じ患者が続けて受けた実績）
export type CoursePair = { first: string, second: string, count: number }

// 候補日から除外する曜日（日・土）
export const EXCLUDED_WEEKDAYS = [0, 6]

// 受付時刻の実績が無いコースの探索範囲
export const RECEPTION_FROM = "08:30"
export const RECEPTION_TO = "12:00"
export const RECEPTION_STEP = 15

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number)
  return h * 60 + m
}
const toHHMM = (minutes: number) => `0${Math.floor(minutes / 60)}`.slice(-2) + ":" + `0${minutes % 60}`.slice(-2)
const emptyIfNull = (v?: string | null) => v ? v : ""
// useMongoQuery の translateEvent と同じ形のカレンダーイベント
const toEvent = (v: KV): CalendarEvent => ({
  id: v._id, start: new Date(v.start), end: new Date(v.end), resourceId: v.modalityID, extendedProps: { ...v, id: v._id },
} as CalendarEvent)
export const contentKey = (v: CourseContent) => [v.modality, v.modality_body1, v.modality_body2, v.modality_contrast].join("|")

/**
 * bookings の実績からコースごとの検査構成を推定する
 * ドック単位(dockID)で検査をまとめ、コース×性別ごとに最も多い構成を採用
 * 予約IDは course_contents の順に採番されるため、ID順に並べてから優先順位順にする（再計算と同じ並び）
 * @param bookings ドック予約を含む予約データ
 * @param dockInfos dockID → 性別・受付時刻
 */
export const analyzeCourses = (bookings: KV[], dockInfos: Record<string, DockInfo | undefined>): CourseProfile[] => {
  const docks: Record<string, { courseName: string, contents: { id: number, content: CourseContent }[] }> = {}
  for (const booking of bookings) {
    if (!DOCK_TYPES.includes(booking.bookingType) || booking.bookingStatus === CANCEL_STATUS) continue
    if (!booking.courseName || !booking.dockID) continue
    const modality = String(booking.modalityID).startsWith("PET-CT") ? "PET-CT" : (CONSULTING_MODALITIES[booking.modalityID] ?? booking.modalityID)
    if (!dockModalityPriority.includes(modality) && !CONSULTING_ORDER.includes(modality)) continue
    const dock = docks[booking.dockID] ||= { courseName: booking.courseName, contents: [] }
    dock.contents.push({
      id: Number(booking._id),
      content: {
        modality,
        modality_body1: emptyIfNull(booking.body1),
        modality_body2: emptyIfNull(booking.body2),
        modality_contrast: emptyIfNull(booking.contrast),
      },
    })
  }

  const courses: Record<string, {
    count: number
    signatures: Partial<Record<Gender | "ALL", Record<string, CoursePattern>>>
    options: Partial<Record<Gender | "ALL", Record<string, CourseOption>>>
    receptionTimes: Set<string>
  }> = {}
  for (const [dockID, dock] of Object.entries(docks)) {
    const contents = sortCourseContents(dock.contents.sort((a, b) => a.id - b.id).map(v => v.content))
    const signature = contents.map(contentKey).join(",")
    const course = courses[dock.courseName] ||= { count: 0, signatures: {}, options: {}, receptionTimes: new Set() }
    course.count++
    const { gender, receptionTime } = dockInfos[dockID] ?? {}
    if (receptionTime && /^\d{1,2}:\d{2}$/.test(receptionTime)) course.receptionTimes.add(toHHMM(toMinutes(receptionTime)))
    for (const group of (gender ? [gender, "ALL"] : ["ALL"]) as (Gender | "ALL")[]) {
      const counter = (course.signatures[group] ||= {})
      const entry = counter[signature] ||= { contents, count: 0, total: 0 }
      entry.count++
      const options = (course.options[group] ||= {})
      for (const key of Array.from(new Set(contents.map(contentKey)))) {
        (options[key] ||= { content: contents.find(v => contentKey(v) === key)!, count: 0 }).count++
      }
    }
  }

  return Object.entries(courses).map(([courseName, course]) => {
    const patterns: CourseProfile["patterns"] = {}
    for (const [group, counter] of Object.entries(course.signatures) as [Gender | "ALL", Record<string, CoursePattern>][]) {
      const entries = Object.values(counter)
      const top = entries.reduce((a, b) => (b.count > a.count ? b : a))
      patterns[group] = { ...top, total: entries.reduce((sum, v) => sum + v.count, 0) }
    }
    const options: CourseProfile["options"] = {}
    for (const [group, counter] of Object.entries(course.options) as [Gender | "ALL", Record<string, CourseOption>][]) {
      const values = Object.values(counter)
      // 再計算と同じ優先順位順、同じモダリティ内は実施件数の多い順
      options[group] = sortCourseContents(values.sort((a, b) => b.count - a.count).map(v => v.content))
        .map(content => values.find(v => v.content === content)!)
    }
    return { courseName, count: course.count, patterns, options, receptionTimes: Array.from(course.receptionTimes).sort() }
  }).sort((a, b) => b.count - a.count)
}

/**
 * 性別に応じたコースの検査構成（性別の実績が無ければ全体の構成）
 */
export const getCourseContents = (profile: CourseProfile, gender: Gender) =>
  (profile.patterns[gender] ?? profile.patterns.ALL)?.contents ?? []

/**
 * 性別に応じたコースの検査一覧（性別の実績が無ければ全体）
 */
export const getCourseOptions = (profile: CourseProfile, gender: Gender) =>
  profile.options[gender] ?? profile.options.ALL ?? []

/**
 * 候補とする受付時刻（実績があればその時刻、無ければ既定の範囲）
 */
export const getReceptionTimes = (profile: CourseProfile) => {
  if (profile.receptionTimes.length) return profile.receptionTimes
  const times: string[] = []
  for (let minutes = toMinutes(RECEPTION_FROM); minutes <= toMinutes(RECEPTION_TO); minutes += RECEPTION_STEP) times.push(toHHMM(minutes))
  return times
}

/**
 * 新規患者の PET-CT の IV 開始時刻を求め、他患者の PET と 10 分以上離れているか確認する
 * checkPetTimeForDock の新規予約時（同一患者の他予約が無い場合）と同じ判定
 * @returns ivCheckin: IV開始時刻, conflict: IV時刻が10分未満の他患者のPET（NGの場合）
 */
export const checkPetIvForNewPatient = (booking: Booking, events: CalendarEvent[]): { ivCheckin: string, conflict?: Booking } => {
  const getTimeStamp = (v: any) => new Date(v).getTime()
  const [startDate, endDate] = getSamedayArea(booking.start)
  const bookings: Booking[] = events
    .filter(v => (startDate.getTime() <= getTimeStamp(v.start) && endDate.getTime() > getTimeStamp(v.start)))
    .map(({ id, extendedProps, ...v }) => ({ ...v, ...extendedProps, _id: id }))
  const ivCheckin = getLatestIvTime(booking)
  const conflict = bookings.find(v => isPet(v) && v._id !== booking._id && v.patientID !== booking.patientID && Math.abs(getMinutesFromMidnight(v.ivCheckin) - getMinutesFromMidnight(ivCheckin)) < 10)
  return { ivCheckin, conflict }
}

// 判定結果。NG の場合は reason に理由（exams は判定できたところまでの配置）
// warnings: OK だが注意が必要なこと（3階更衣室が満室など。再計算でも警告が出る）
export type PersonEvaluation = { exams: ScheduledExam[], reason?: string, stayMinutes?: number, warnings?: string[] }

const formatTime = (v: Date | string) => {
  const date = new Date(v)
  return `${date.getHours()}:${`0${date.getMinutes()}`.slice(-2)}`
}
const examLabel = (exam: { modalityID: string, body2: string }) => `${exam.modalityID} ${exam.body2}`.trim()

/**
 * 受付時刻を指定して「再計算」を行い、登録時のチェックを通るか判定する
 * @param courseName 3階更衣室の空きの判定に使う
 * @param dockID 更衣室の利用者として数えるときの、自分のドック予約番号
 * @param maxStayMinutes 受付から全検査終了までの上限(分)。超える場合は NG
 */
export const evaluateDockPerson = (contents: CourseContent[], reception: Date, events: CalendarEvent[], baseResources: KV[], patientID: string, courseName?: string, dockID?: string, maxStayMinutes?: number): PersonEvaluation => {
  let duration: KV
  let overtime: boolean
  let ivUnavailable: boolean
  let waitOver: WaitOver | undefined
  try {
    ({ duration, overtime, ivUnavailable, waitOver } = calcDockSchedule({
      contents,
      reception,
      events,
      getModalityTime: (modality, body1, body2, contrast) => getDockModalityTime(baseResources, modality, body1, body2, contrast),
      nonScheduleModality,
      dockID,
    }))
  } catch (e) {
    return { exams: [], reason: "所要時間マスタ(modality_dock.json)に無い検査があります" }
  }

  const exams: ScheduledExam[] = []
  for (const key of contents) {
    const modalityID = key.modality === "PET-CT" ? "PET-CT_1" : key.modality
    const modalityAll = modalityID + key.modality_body1 + key.modality_body2 + key.modality_contrast
    // 時間を管理しない検査は再計算の対象外。予約確定時と同じ時刻（診療予約は固定の枠、ほかは受付10分後〜20分後）
    const schedule = duration[modalityAll] ?? {
      ...(CONSULTING_ORDER.includes(key.modality)
        ? getConsultingSlot(contents, key.modality, reception)
        : { start: new Date(reception.getTime() + 10 * 60000), end: new Date(reception.getTime() + 20 * 60000) }),
      no_schedule: true,
    }
    exams.push({
      modalityID,
      body1: key.modality_body1,
      body2: key.modality_body2,
      contrast: key.modality_contrast,
      start: schedule.start,
      end: schedule.end,
      noSchedule: schedule.no_schedule,
    })
  }
  // 実施する順（時刻順）に並べる
  exams.sort((a, b) => a.start.getTime() - b.start.getTime())

  const stay = getDockStay(reception, duration)
  // 3階更衣室が満室でも予約はできる（再計算と同じく警告のみ）
  const warnings: string[] = []
  const lockerInUse = checkLockerFull(courseName, stay, events, dockID)
  if (lockerInUse) {
    warnings.push(`3階更衣室が満室（${formatTime(reception)}〜${formatTime(stay.end)}の間にCMC会員コース・CMC後日検査の受診者が${lockerInUse}名）`)
  }
  if (overtime) {
    return { exams, reason: `${DOCK_EXAM_END}を過ぎる（再計算は止まる）` }
  }
  if (ivUnavailable) {
    return { exams, reason: 'PET-CT の IV を行える時刻（決まった時刻のみ）に空きがない（再計算は止まる）' }
  }
  if (waitOver) {
    return { exams, reason: `検査の間の待ち時間が${MAX_WAIT_MINUTES}分を超える（${waitOver.exam} の前に${waitOver.minutes}分、再計算は止まる）` }
  }
  // 婦人科（US(L)・子宮細胞診・HPV）は火・水・金の 9:15〜12:30（再計算も止まる）
  const gynecology = checkGynecology(contents, reception, duration)
  if (gynecology) {
    return { exams, reason: gynecology }
  }
  const esLowerCount = checkEsLowerFull(contents, events, dockID)
  if (esLowerCount) {
    return { exams, reason: `ES下部がこの日すでに${esLowerCount}件（1日${ES_LOWER_DAILY_LIMIT}件まで、再計算でも警告が出る）` }
  }
  if (duplicateCheck(duration)) {
    return { exams, reason: "本人の検査同士の時刻が重なる（仮予約・予約確定時に警告が出る）" }
  }
  // 再計算の配置が既存予約と重なっていないか（再計算では検出されない）
  for (const exam of exams) {
    if (exam.noSchedule) continue
    const overlap = events.find(v =>
      v.resourceId === exam.modalityID &&
      new Date(v.start as Date).getTime() < exam.end.getTime() &&
      new Date(v.end as Date).getTime() > exam.start.getTime()
    )
    if (overlap) {
      return { exams, reason: `${examLabel(exam)} ${formatTime(exam.start)}〜${formatTime(exam.end)} が既存予約(ID:${overlap.id}) ${formatTime(overlap.start as Date)}〜${formatTime(overlap.end as Date)} と重なる（再計算の配置不具合）` }
    }
  }
  // 受付から全検査終了までの上限（2日連続の2日目は4時間以内）
  if (maxStayMinutes !== undefined && stay.minutes > maxStayMinutes) {
    const hm = (minutes: number) => `${Math.floor(minutes / 60)}時間${minutes % 60 ? `${minutes % 60}分` : ''}`
    return { exams, reason: `受付から全検査終了まで${hm(stay.minutes)}で、${hm(maxStayMinutes)}を超える`, stayMinutes: stay.minutes }
  }
  // PET-CT の IV（仮予約・予約確定時と同じチェック）
  for (const exam of exams) {
    const modalityAll = exam.modalityID + exam.body1 + exam.body2 + exam.contrast
    if (!isPetCourse(modalityAll)) continue
    const [modality, body2, contrast] = parseModalityAll(modalityAll)
    const { ivCheckin, conflict } = checkPetIvForNewPatient({ start: exam.start, end: exam.end, patientID, modalityID: modality, body2, contrast }, events)
    if (conflict) {
      return { exams, reason: `${examLabel(exam)} のIV ${ivCheckin} が他患者のPET(ID:${conflict._id} ${conflict.modalityID}) のIV ${conflict.ivCheckin} と10分未満（仮予約・予約確定時にエラー）` }
    }
    exam.ivCheckin = ivCheckin
  }
  return { exams, stayMinutes: stay.minutes, warnings: warnings.length ? warnings : undefined }
}

/**
 * 受付時刻を指定して「再計算」を行い、登録時のチェックを通るか確認する
 * @returns 予約時刻。NGなら undefined
 */
export const scheduleDockPerson = (contents: CourseContent[], reception: Date, events: CalendarEvent[], baseResources: KV[], patientID: string, courseName?: string) => {
  const { exams, reason } = evaluateDockPerson(contents, reception, events, baseResources, patientID, courseName)
  return reason ? undefined : exams
}

/**
 * 指定日に全員分の検査が収まるか試す
 * 1人目の受付時刻を早い順に試し、2人目以降は（受付時刻の差の上限内で）最も早い受付時刻で配置する
 * 配置した検査は、次の人にとって既存予約として扱う（1人ずつ登録したときと同じ）
 * @param bookings その日を含む予約データ（キャンセル除く）
 * @param maxGap 1人目との受付時刻の差の上限(分)。undefined なら制限なし
 */
const prepareDay = (date: Date, people: Person[], bookings: KV[], baseResources: KV[]) => {
  // /dock 画面の1日表示と同じく、その日0時〜24時の予約
  const dayStart = new Date(date)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)
  const dayEvents: CalendarEvent[] = bookings
    .filter(v => v.bookingStatus !== CANCEL_STATUS && new Date(v.start) >= dayStart && new Date(v.start) < dayEnd)
    .map(toEvent)

  const receptionAt = (time: string) => {
    const reception = new Date(dayStart)
    reception.setHours(Math.floor(toMinutes(time) / 60), toMinutes(time) % 60)
    return reception
  }
  // 同じ人・同じ受付時刻・同じ前提（先に配置した家族の受付時刻 = state）の判定は、結果を使い回す
  const memo = new Map<string, PersonEvaluation & { reception: Date }>()
  const evaluate = (index: number, time: string, events: CalendarEvent[], state = '') => {
    const key = `${index}|${time}|${state}`
    const cached = memo.get(key)
    if (cached) return cached
    const reception = receptionAt(time)
    const patientID = `candidate-${index + 1}`
    const result = { reception, ...evaluateDockPerson(people[index].contents, reception, events, baseResources, patientID, people[index].courseName, patientID, people[index].maxStayMinutes) }
    memo.set(key, result)
    return result
  }
  const place = (index: number, time: string, events: CalendarEvent[], state = ''): PersonSchedule | undefined => {
    const { reception, exams, reason, stayMinutes = 0, warnings } = evaluate(index, time, events, state)
    if (reason) return undefined
    return { index, gender: people[index].gender, reception, exams, stayMinutes, warnings }
  }
  // 配置した人の検査を予約済みとして追加（更衣室の利用者としても数える）
  const register = (person: PersonSchedule, events: CalendarEvent[]) => {
    const patientID = `candidate-${person.index + 1}`
    const checkin = `${`0${person.reception.getHours()}`.slice(-2)}:${`0${person.reception.getMinutes()}`.slice(-2)}`
    for (const exam of person.exams) {
      events.push(toEvent({
        _id: `${patientID}-${exam.modalityID}-${exam.start.getTime()}`, ...exam, patientID, bookingType: DOCK_TYPES[0],
        dockID: patientID, courseName: people[person.index].courseName, checkin,
      }))
    }
  }
  return { dayEvents, evaluate, place, register }
}

type PreparedDay = ReturnType<typeof prepareDay>

const runDay = (day: PreparedDay, people: Person[], maxGap: number | undefined): PersonSchedule[] | undefined => {
  const { dayEvents, place, register } = day
  for (const firstTime of people[0].receptionTimes) {
    const events = [...dayEvents]
    const first = place(0, firstTime, events)
    if (!first) continue
    register(first, events)
    const times = [firstTime]
    const persons = [first]
    for (let index = 1; index < people.length; index++) {
      let placed: PersonSchedule | undefined
      for (const time of people[index].receptionTimes) {
        if (maxGap !== undefined && Math.abs(toMinutes(time) - toMinutes(firstTime)) > maxGap) continue
        if ((placed = place(index, time, events, times.join(',')))) {
          times.push(time)
          break
        }
      }
      if (!placed) break
      register(placed, events)
      persons.push(placed)
    }
    if (persons.length === people.length) return persons
  }
  return undefined
}

export const scheduleDay = (date: Date, people: Person[], bookings: KV[], baseResources: KV[], maxGap?: number): PersonSchedule[] | undefined =>
  runDay(prepareDay(date, people, bookings, baseResources), people, maxGap)

// 候補にできる日か（ドック実績のある曜日で、土日・祝日でない）
const isDockDay = (date: Date, weekdays: number[]) =>
  weekdays.includes(date.getDay()) && !EXCLUDED_WEEKDAYS.includes(date.getDay()) && !getHolidayName(date)

/**
 * 翌営業日（週末・祝日をはさむ場合はその次の候補にできる日）。2週間以内に無ければ undefined
 */
export const nextDockDay = (date: Date, weekdays: number[]) => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  for (let k = 0; k < 14; k++) {
    next.setDate(next.getDate() + 1)
    if (isDockDay(next, weekdays)) return next
  }
  return undefined
}

/**
 * 指定日の候補。土日・祝日、対象外の曜日、全員が入らない日は undefined
 * @param secondDay 2日連続の場合の2日目の対象者。翌営業日に全員が入らなければ undefined
 * @param until 2日目がこの日より後になる場合は undefined（予約データを読み込んでいない範囲のため）
 */
export const candidateForDay = (date: Date, people: Person[], bookings: KV[], baseResources: KV[], weekdays: number[], maxGap?: number, secondDay?: Person[], until?: Date): Candidate | undefined => {
  const first = candidateForOneDay(date, people, bookings, baseResources, weekdays, maxGap)
  if (!first || !secondDay?.length) return first
  const nextDate = nextDockDay(date, weekdays)
  if (!nextDate || (until && nextDate > until)) return undefined
  const second = candidateForOneDay(nextDate, secondDay, bookings, baseResources, weekdays, maxGap)
  if (!second) return undefined
  return { ...first, second: { date: second.date, persons: second.persons } }
}

const candidateForOneDay = (date: Date, people: Person[], bookings: KV[], baseResources: KV[], weekdays: number[], maxGap?: number): Candidate | undefined => {
  if (!isDockDay(date, weekdays)) return undefined
  const persons = runDay(prepareDay(date, people, bookings, baseResources), people, maxGap)
  return persons && { date: new Date(date), persons }
}

export type DiagnoseAttempt = { time: string, reception: Date, exams: ScheduledExam[], reason?: string, stayMinutes?: number, warnings?: string[] }

/**
 * 指定日について、受付時刻ごとの判定結果（NGの理由）を返す
 * 1人ずつ受付時刻を早い順に試し、OKになった時刻で配置して次の人へ進む（受付時刻の差の上限は考慮しない）
 * @returns 人ごとの試行結果。OKになった受付時刻まで（全てNGなら全受付時刻）
 */
export const diagnoseDay = (date: Date, people: Person[], bookings: KV[], baseResources: KV[]): DiagnoseAttempt[][] => {
  const { dayEvents, evaluate, register } = prepareDay(date, people, bookings, baseResources)
  const events = [...dayEvents]
  return people.map((person, index) => {
    const attempts: DiagnoseAttempt[] = []
    for (const time of person.receptionTimes) {
      const attempt = { time, ...evaluate(index, time, events) }
      attempts.push(attempt)
      if (!attempt.reason) {
        register({ index, gender: person.gender, reception: attempt.reception, exams: attempt.exams, stayMinutes: attempt.stayMinutes ?? 0 }, events)
        break
      }
    }
    return attempts
  })
}

/**
 * from〜until で全員分が収まる日を limit 件まで返す（土日・祝日は除外）
 * 日付の近い順（受付から全検査終了までの上限を超える日は、各人の maxStayMinutes で除外）
 * @param weekdays 対象とする曜日(0=日)。ドック実績のある曜日を想定
 * @param maxGap 1人目との受付時刻の差の上限(分)
 */
export const findCandidates = ({ from, until, people, secondDay, bookings, baseResources, weekdays, maxGap, limit = 5 }: {
  from: Date, until: Date, people: Person[], secondDay?: Person[], bookings: KV[], baseResources: KV[], weekdays: number[], maxGap?: number, limit?: number
}): Candidate[] => {
  const candidates: Candidate[] = []
  const date = new Date(from)
  date.setHours(0, 0, 0, 0)
  while (date <= until && candidates.length < limit) {
    const candidate = candidateForDay(date, people, bookings, baseResources, weekdays, maxGap, secondDay, until)
    if (candidate) candidates.push(candidate)
    date.setDate(date.getDate() + 1)
  }
  return candidates
}

/**
 * findCandidates と同じ探索を、1日ごとに画面へ処理を返しながら行う（画面が固まらないように）
 * @param onProgress 探索した日数・全日数
 * @param isCancelled true を返すと探索を打ち切り、それまでの結果を返す
 */
export const findCandidatesAsync = async ({ from, until, people, secondDay, bookings, baseResources, weekdays, maxGap, limit = 5, onProgress, isCancelled }: {
  from: Date, until: Date, people: Person[], secondDay?: Person[], bookings: KV[], baseResources: KV[], weekdays: number[], maxGap?: number, limit?: number,
  onProgress?: (done: number, total: number) => void, isCancelled?: () => boolean
}): Promise<Candidate[]> => {
  const candidates: Candidate[] = []
  const date = new Date(from)
  date.setHours(0, 0, 0, 0)
  const total = Math.floor((until.getTime() - date.getTime()) / 864e5) + 1
  let done = 0
  while (date <= until && candidates.length < limit && !isCancelled?.()) {
    const candidate = candidateForDay(date, people, bookings, baseResources, weekdays, maxGap, secondDay, until)
    if (candidate) candidates.push(candidate)
    date.setDate(date.getDate() + 1)
    onProgress?.(++done, total)
    await new Promise(resolve => setTimeout(resolve, 0))
  }
  return candidates
}

/**
 * ドック実績のある曜日（休診曜日を除外するため）
 */
export const getDockWeekdays = (bookings: KV[]) => {
  const weekdays = new Set<number>()
  for (const booking of bookings) {
    if (DOCK_TYPES.includes(booking.bookingType) && booking.bookingStatus !== CANCEL_STATUS) {
      weekdays.add(new Date(booking.start).getDay())
    }
  }
  return Array.from(weekdays).sort()
}

/**
 * 同じ患者が続けて受けたドックのコースの組み合わせ（1日目 → 2日目）を、件数の多い順に返す
 * 受診日の間が4日以内（週末・祝日をはさむ翌営業日を含む）で、コースが違うものを数える
 */
export const analyzeCoursePairs = (bookings: KV[]): CoursePair[] => {
  const dateKey = (v: Date) => `${v.getFullYear()}-${`0${v.getMonth() + 1}`.slice(-2)}-${`0${v.getDate()}`.slice(-2)}`
  const visits: Record<string, Record<string, { date: Date, courseName: string }>> = {}
  for (const booking of bookings) {
    if (!DOCK_TYPES.includes(booking.bookingType) || booking.bookingStatus === CANCEL_STATUS) continue
    if (!booking.patientID || !booking.courseName) continue
    const date = new Date(booking.start)
    date.setHours(0, 0, 0, 0)
    ;(visits[booking.patientID] ||= {})[dateKey(date)] ||= { date, courseName: booking.courseName }
  }
  const counts: Record<string, CoursePair> = {}
  for (const byDate of Object.values(visits)) {
    const days = Object.values(byDate).sort((a, b) => a.date.getTime() - b.date.getTime())
    for (let k = 1; k < days.length; k++) {
      const [first, second] = [days[k - 1], days[k]]
      if (first.courseName === second.courseName || second.date.getTime() - first.date.getTime() > 4 * 864e5) continue
      ;(counts[`${first.courseName}\n${second.courseName}`] ||= { first: first.courseName, second: second.courseName, count: 0 }).count++
    }
  }
  return Object.values(counts).sort((a, b) => b.count - a.count)
}
